import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Button, Badge } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { axiosHelper } from '../../../stores/helpers/axios_helper';
import { toast } from 'react-toastify';
import { useAuth } from '../../../stores';
import { t } from 'i18next';

const THEME_COLOR = '#c59253';

const PlanSelection = ({ state, updateState, onNext, isCompact = false }) => {
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selecting, setSelecting] = useState(null);
    const [currentPlanId, setCurrentPlanId] = useState(null);

    useEffect(() => {
        if (user && user.plan_id) {
            setCurrentPlanId(user.plan_id);
        }
    }, [user]);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await axiosHelper.get("/plan/standard");
            setPlans(response);
            setLoading(false);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load plans");
            setLoading(false);
        }
    };

    const handleSelectPlanDirectly = async (plan) => {
        if (plan.id === currentPlanId) return;
        setSelecting(plan.id);
        try {
            await axiosHelper.post("/payment/assign-plan", { planId: plan.id });
            const { refreshUser } = useAuth.getState();
            await refreshUser();
            toast.success(`${plan.name} selected!`);
        } catch (error) {
            console.error(error);
            toast.error("Failed to assign plan");
        } finally {
            setSelecting(null);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const cardVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 },
        hover: {
            y: -4,
            transition: { type: 'spring', stiffness: 300 }
        }
    };

    if (loading) {
        return (
            <div className={`d-flex justify-content-center align-items-center ${isCompact ? 'py-4' : 'py-5'}`}>
                <div className="spinner-border" style={{ color: THEME_COLOR }} role="status">
                    <span className="visually-hidden">Loading plans...</span>
                </div>
            </div>
        );
    }

    const FeatureItem = ({ label, value, isEnabled = true }) => {
        const isNotAvailable = !isEnabled || value === 0;

        return (
            <li className="d-flex align-items-center" style={{ fontSize: '13px', opacity: isNotAvailable ? 0.6 : 1 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="me-2 flex-shrink-0">
                    {isNotAvailable ? (
                        <path d="M18 6L6 18M6 6l12 12" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    ) : (
                        <path d="M20 6L9 17L4 12" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                </svg>
                <span style={{ color: isNotAvailable ? '#9ca3af' : '#374151', textDecoration: isNotAvailable ? 'line-through' : 'none' }}>
                    {label}
                </span>
            </li>
        );
    };

    return (
        <div className={isCompact ? "py-2" : "py-4"}>
            {!isCompact && (
                <div className="text-center mb-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="d-inline-block mb-2">
                            <span style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                letterSpacing: '1px',
                                color: THEME_COLOR,
                                textTransform: 'uppercase'
                            }}>
                                Pricing
                            </span>
                        </div>
                        <h2 className="fw-semibold mb-2" style={{ fontSize: '28px', color: '#1f2937' }}>
                            Choose Your Plan
                        </h2>
                        <p className="text-muted mb-0" style={{ fontSize: '14px', maxWidth: '500px', margin: '0 auto' }}>
                            Select the plan that fits your needs. Upgrade anytime as you grow.
                        </p>
                    </motion.div>
                </div>
            )}

            <motion.div
                className="row justify-content-center gx-3"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {plans.map((plan) => {
                    const isEnterprise = plan.name === 'Enterprise';
                    const isSelecting = selecting === plan.id;
                    const isCurrent = plan.id === currentPlanId;

                    return (
                        <Col xl={4} lg={4} md={6} key={plan.id} className="mb-3">
                            <motion.div
                                variants={cardVariants}
                                whileHover={!isCurrent ? "hover" : ""}
                                className="h-100"
                            >
                                <Card
                                    className=" rounded-4 overflow-hidden position-relative h-100"
                                    style={{
                                        borderColor: isCurrent ? THEME_COLOR : '#e5e7eb',
                                        borderWidth: isCurrent ? '2px' : '1px',
                                        backgroundColor: '#ffffff',
                                        boxShadow: isCurrent
                                            ? '0 4px 12px rgba(197, 146, 83, 0.15)'
                                            : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {isCurrent ? (
                                        <div
                                            className="position-absolute top-0 end-0 text-white px-2 py-1 fw-bold"
                                            style={{
                                                borderBottomLeftRadius: '8px',
                                                zIndex: 10,
                                                backgroundColor: THEME_COLOR,
                                                fontSize: '9px',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            CURRENT
                                        </div>
                                    ) : isEnterprise && (
                                        <div
                                            className="position-absolute top-0 end-0 text-white px-2 py-1 fw-bold"
                                            style={{
                                                borderBottomLeftRadius: '8px',
                                                zIndex: 10,
                                                backgroundColor: THEME_COLOR,
                                                fontSize: '9px',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            POPULAR
                                        </div>
                                    )}

                                    <CardBody className={isCompact ? "p-3 d-flex flex-column" : "p-4 d-flex flex-column"}>
                                        <div className="mb-3">
                                            <h5 className="fw-semibold mb-2" style={{ fontSize: '18px', color: '#111827' }}>
                                                {plan.name}
                                            </h5>
                                            <div className="d-flex align-items-baseline">
                                                <h3 className="fw-bold mb-0" style={{ color: '#111827', fontSize: '32px' }}>
                                                    QAR {plan.price}
                                                </h3>
                                                <span className="text-muted ms-1" style={{ fontSize: '13px' }}>/mo</span>
                                            </div>
                                        </div>

                                        <ul className="list-unstyled mb-3 flex-grow-1 vstack gap-2">
                                            <FeatureItem
                                                label={`${plan.max_sites} Site${plan.max_sites > 1 ? 's' : ''}`}
                                                value={plan.max_sites}
                                            />
                                            <FeatureItem
                                                label={`${plan.max_spaces} Spaces`}
                                                value={plan.max_spaces}
                                            />


                                            <FeatureItem
                                                label={`${plan.max_stations} Service Stations`}
                                                value={plan.max_stations}
                                            />
                                            <FeatureItem
                                                label="Order"
                                                isEnabled={(plan.service_level ?? 0) >= 3}
                                            />
                                            <FeatureItem
                                                label="Splash Screen"
                                                isEnabled={plan.can_splash}
                                            />
                                        </ul>

                                        {/* <Button
                                            size="sm"
                                            className="w-100 rounded-2 fw-semibold py-2 d-flex align-items-center justify-content-center border-0"
                                            onClick={() => handleSelectPlanDirectly(plan)}
                                            disabled={!!selecting || isCurrent}
                                            style={{
                                                backgroundColor: isCurrent ? '#f3f4f6' : THEME_COLOR,
                                                color: isCurrent ? '#9ca3af' : '#fff',
                                                fontSize: '14px',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isCurrent && !selecting) {
                                                    e.currentTarget.style.backgroundColor = '#b8834a';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isCurrent && !selecting) {
                                                    e.currentTarget.style.backgroundColor = THEME_COLOR;
                                                }
                                            }}
                                        >
                                            {isSelecting ? (
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                            ) : isCurrent ? (
                                                t("Active Plan")
                                            ) : (
                                                <>
                                                    {t("Select Plan")}
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="ms-1">
                                                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </>
                                            )}
                                        </Button> */}
                                    </CardBody>
                                </Card>

                            </motion.div>
                        </Col>
                    );
                })}

                <p className='text-center'>
                    Please contact admin for more details
                </p>
            </motion.div>
        </div>
    );
};

export default PlanSelection;